'use client'

import { PaymentSuccessTracker } from '@/components/payment-success-tracker'
import { Badge } from '@/components/ui/badge'
import { useWorkspaces } from '@/hooks/subscriptions/use-workspaces'
import { useCurrentUser } from '@/hooks/use-current-user'
import {
  creditTiers,
  domainTiers,
  platinumBaseCredit,
  premiumBaseCredit,
} from '@/lib/pricing-v3/_const'
import {
  calculatePricingV3Price,
  getSubscriptionAction as getSubscriptionActionHelper,
} from '@/lib/pricing-v3/helper'
import {
  collectTrackingParams,
  createEcommerceItem,
  trackBeginCheckout,
  trackViewPricing,
} from '@/lib/tracking'
import type { SubscriptionDetails } from '@/lib/tracking/types'
import { getRewardfulCouponId, getRewardfulRefId } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { UnderstandingCredits } from './_credits'
import { PricingFAQ } from './_faq'
import { PricingHero } from './_hero'
import { BillingToggle } from './billing-toggle'
import { FeatureComparisonTable } from './feature-comparison-table'
import { IncompleteSubscriptionBanner } from './incomplete-subscription-banner'
import { PaymentIssueBanner } from './payment-issue-banner'
import { PricingCards } from './pricing-cards'
import { WorkspaceSelector } from './workspace-selector'

export default function PricingPage({
  isModal = false,
  minRequiredPlan,
  trigger,
  projectId,
  workspaceId,
  showEnterpriseCard = false,
  showDomainOptions = false,
}: {
  isModal?: boolean
  minRequiredPlan?: 'basic' | 'premium' | 'platinum'
  trigger?: string
  projectId?: string
  workspaceId?: string
  showEnterpriseCard?: boolean
  showDomainOptions?: boolean
}) {
  const t = useTranslations('pricing-v3')

  // Check if user is logged in
  const { data: user } = useCurrentUser()
  const isLoggedIn = !!user

  // Workspace selection state
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    null,
  )

  // Get all workspaces with their subscription data (only if user is logged in)
  // Use ownedWorkspaces for pricing page since only owners can manage subscriptions
  const {
    ownedWorkspaces: workspaces,
    defaultWorkspaceId,
    loading: workspacesLoading,
    mutate: mutateWorkspaces,
  } = useWorkspaces()

  // Get the selected workspace and its subscription
  const selectedWorkspace = selectedWorkspaceId
    ? workspaces.find((w) => w._id.toString() === selectedWorkspaceId)
    : null
  const curSubscription = selectedWorkspace?.subscription
  const curCreditPerMonth = curSubscription?.credits_per_month
  const curDomains = curSubscription?.limits?.max_domains

  // Initialize selected workspace based on workspaceId, projectId, or defaults
  useEffect(() => {
    if (workspaces.length > 0) {
      // If workspaceId is provided directly, use it first (override current selection if different)
      if (workspaceId) {
        const workspaceExists = workspaces.some(
          (w) => w._id.toString() === workspaceId,
        )

        if (workspaceExists && selectedWorkspaceId !== workspaceId) {
          setSelectedWorkspaceId(workspaceId)
          return
        } else if (!workspaceExists) {
          console.log('[PricingPage] Workspace not found with ID:', workspaceId)
        }
      }

      // If no workspaceId provided and no selection yet, continue with other logic
      if (!workspaceId && !selectedWorkspaceId) {
        // If projectId is provided, find the workspace that contains this project
        if (projectId) {
          const workspaceWithProject = workspaces.find((w) =>
            w.projects?.some((p) => p._id === projectId),
          )

          if (workspaceWithProject) {
            setSelectedWorkspaceId(workspaceWithProject._id.toString())
            return
          } else {
            console.log(
              '[PricingPage] No workspace found containing project:',
              projectId,
            )
          }
        }

        // Otherwise use the default workspace (first with active subscription, or just first)
        const fallbackId = defaultWorkspaceId || workspaces[0]._id.toString()
        console.log('[PricingPage] Using fallback workspace:', fallbackId)
        setSelectedWorkspaceId(fallbackId)
      }
    }
  }, [
    workspaces,
    selectedWorkspaceId,
    defaultWorkspaceId,
    projectId,
    workspaceId,
  ])

  useEffect(() => {
    if (curSubscription) {
      // Set billing cycle based on current subscription
      setIsAnnual(curSubscription.billing_cycle === 'yearly')

      setBasicCredits(curSubscription.credits_per_month?.toString() || '200')
      if (
        curSubscription.credits_per_month &&
        curSubscription.credits_per_month >= premiumBaseCredit
      ) {
        setPremiumCredits(
          curSubscription.credits_per_month?.toString() ||
            premiumBaseCredit.toString(),
        )
      } else {
        setPremiumCredits(premiumBaseCredit.toString())
      }
      if (
        curSubscription.credits_per_month &&
        curSubscription.credits_per_month >= platinumBaseCredit
      ) {
        setPlatinumCredits(
          curSubscription.credits_per_month?.toString() ||
            platinumBaseCredit.toString(),
        )
      } else {
        setPlatinumCredits(platinumBaseCredit.toString())
      }
      setBasicDomains(curSubscription.limits?.max_domains?.toString() || '1')
      setPremiumDomains(curSubscription.limits?.max_domains?.toString() || '1')
      setPlatinumDomains(curSubscription.limits?.max_domains?.toString() || '1')
    } else {
      // Reset to defaults when switching to a workspace without a subscription
      setIsAnnual(true)
      setBasicCredits('200')
      setPremiumCredits(premiumBaseCredit.toString())
      setPlatinumCredits(platinumBaseCredit.toString())
      setBasicDomains('1')
      setPremiumDomains('1')
      setPlatinumDomains('1')
    }
  }, [curSubscription])

  // Track pricing page view
  useEffect(() => {
    trackViewPricing()
  }, [])

  const creditOptions = creditTiers.map((tier) => ({
    value: tier.value.toString(),
    label: t('options.credits_per_month', { credits: tier.value }),
    price: tier.price,
  }))

  const domainOptions = domainTiers.map((option) => ({
    value: option.value.toString(),
    label:
      option.value >= 999
        ? t('options.domains_unlimited')
        : t('options.domain_singular', { count: option.value }),
    price: option.price,
  }))

  const basicCreditOptions = creditOptions
  const premiumCreditOptions = creditOptions.filter(
    (tier) => Number.parseInt(tier.value) >= premiumBaseCredit,
  )
  const platinumCreditOptions = creditOptions.filter(
    (tier) => Number.parseInt(tier.value) >= platinumBaseCredit,
  )

  // Use the filtered subscription data for the selected workspace
  // Only treat active or past_due subscriptions as current plans
  // Canceled subscriptions should be treated as 'free' to allow re-purchase
  const currentPlan =
    curSubscription &&
    (curSubscription.status === 'active' ||
      curSubscription.status === 'past_due')
      ? curSubscription.plan
      : 'free'
  const subscriptionLoading = workspacesLoading
  const hasPaymentIssue =
    curSubscription?.status === 'past_due' ||
    curSubscription?.status === 'unpaid'
  const hasIncompleteSubscription =
    curSubscription?.status === 'incomplete' ||
    curSubscription?.status === 'incomplete_expired'
  const incompleteSubscription =
    curSubscription?.status === 'incomplete' ||
    curSubscription?.status === 'incomplete_expired'
      ? curSubscription
      : null
  const subscriptionStatus = curSubscription?.status

  const [isAnnual, setIsAnnual] = useState(true) // default to annual
  const [basicCredits, setBasicCredits] = useState(
    curCreditPerMonth?.toString() || '200',
  )
  const [premiumCredits, setPremiumCredits] = useState(
    curCreditPerMonth?.toString() || premiumBaseCredit.toString(),
  )
  const [platinumCredits, setPlatinumCredits] = useState(
    curCreditPerMonth?.toString() || platinumBaseCredit.toString(),
  )
  const [basicDomains, setBasicDomains] = useState(
    curDomains?.toString() || '1',
  )
  const [premiumDomains, setPremiumDomains] = useState(
    curDomains?.toString() || '1',
  )
  const [platinumDomains, setPlatinumDomains] = useState(
    curDomains?.toString() || '1',
  )

  const [openFaqItems, setOpenFaqItems] = useState<number[]>([])
  const [loadingPlan, setLoadingPlan] = useState<
    'basic' | 'premium' | 'platinum' | null
  >(null)
  const [showDomainToggle, setShowDomainToggle] = useState(false)

  // Determine whether to show domain select
  // Show if: 1) explicitly passed via props, OR 2) user has curDomains > 1, OR 3) user manually toggled it on
  const shouldShowDomainSelect =
    showDomainOptions || !!(curDomains && curDomains > 1) || showDomainToggle

  /**
   * Determine the subscription action type (new, current, upgrade, downgrade, change)
   * Wrapper around the helper function to pass component state
   * @param targetPlan - The plan to check against (basic, premium, or platinum)
   * @param targetCredits - Optional credits override
   * @param targetDomains - Optional domains override
   * @returns The type of subscription action
   */
  const getSubscriptionAction = (
    targetPlan: 'basic' | 'premium' | 'platinum',
    targetCredits?: string,
    targetDomains?: string,
  ) => {
    return getSubscriptionActionHelper({
      subscription: curSubscription || null,
      currentPlan: currentPlan || 'free',
      targetPlan,
      targetCredits,
      targetDomains,
      selectedCredits: {
        basic: basicCredits,
        premium: premiumCredits,
        platinum: platinumCredits,
      },
      selectedDomains: {
        basic: basicDomains,
        premium: premiumDomains,
        platinum: platinumDomains,
      },
      isAnnual,
    })
  }
  /**
   * Get the button text based on subscription action
   * Mobile: shorter CTA text for better UX
   * Desktop: detailed text with pricing
   * @param plan - Plan type (basic, premium, or platinum)
   * @returns Localized button text
   */
  const getButtonText = (plan: 'basic' | 'premium' | 'platinum') => {
    if (loadingPlan === plan) return t('buttons.processing')

    const action = getSubscriptionAction(plan)
    const targetPrice = calculatePrice(plan)
    const currentCredits =
      curSubscription?.credits_per_month?.toString() || '200'
    const currentDomains =
      curSubscription?.limits?.max_domains?.toString() || '1'
    const currentPrice =
      currentPlan && currentPlan !== 'free'
        ? calculatePricingV3Price({
            plan: currentPlan,
            credits: currentCredits,
            domains: currentDomains,
            billingInterval: isAnnual ? 'yearly' : 'monthly',
          })
        : 0

    // Detect mobile viewport
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768

    switch (action) {
      case 'current':
        return t('buttons.current_configuration')
      case 'upgrade':
        // 检查是否只是billing cycle变更（仅限同tier）
        if (currentPlan === plan) {
          const currentCredits =
            curSubscription?.credits_per_month?.toString() || '200'
          const currentDomains =
            curSubscription?.limits?.max_domains?.toString() || '1'
          const selectedCredits =
            plan === 'basic'
              ? basicCredits
              : plan === 'premium'
                ? premiumCredits
                : platinumCredits
          const selectedDomains =
            plan === 'basic'
              ? basicDomains
              : plan === 'premium'
                ? premiumDomains
                : platinumDomains
          const currentBillingCycle =
            curSubscription?.billing_cycle || 'monthly'
          const targetBillingCycle = isAnnual ? 'yearly' : 'monthly'

          if (
            selectedCredits === currentCredits &&
            selectedDomains === currentDomains &&
            currentBillingCycle !== targetBillingCycle
          ) {
            // 只是billing cycle变更
            if (isMobile) {
              return targetBillingCycle === 'yearly'
                ? t('buttons.switch_annual_mobile')
                : t('buttons.switch_monthly_mobile')
            }
            return targetBillingCycle === 'yearly'
              ? t('buttons.switch_to_annual')
              : t('buttons.switch_to_monthly')
          }
        }

        // Different CTAs based on current plan vs new plan
        if (currentPlan === 'free') {
          // New subscriber from free
          if (plan === 'basic') return t('buttons.upgrade_to_basic')
          if (plan === 'premium') return t('buttons.upgrade_to_premium')
          if (plan === 'platinum') return t('buttons.upgrade_to_platinum')
        }

        // Existing subscriber upgrading
        if (isMobile) {
          return t('buttons.upgrade_mobile', {
            amount: (targetPrice - currentPrice).toFixed(2),
          })
        }

        // Show price difference for same tier config change or cross-tier upgrade
        return currentPlan === plan
          ? t('buttons.upgrade_configuration', {
              amount: (targetPrice - currentPrice).toFixed(2),
            })
          : t('buttons.upgrade_to_premium') // Default upgrade text
      case 'downgrade':
        // Show price savings
        if (isMobile) {
          return t('buttons.downgrade_mobile', {
            amount: (currentPrice - targetPrice).toFixed(2),
          })
        }

        return t('buttons.downgrade_configuration', {
          amount: (currentPrice - targetPrice).toFixed(2),
        })
      case 'change':
        return t('buttons.update_configuration')
      default:
        // New subscribers - clear action-oriented CTAs
        if (plan === 'basic') return t('buttons.upgrade_to_basic')
        if (plan === 'premium') return t('buttons.upgrade_to_premium')
        if (plan === 'platinum') return t('buttons.upgrade_to_platinum')
        return t('buttons.subscribe_now')
    }
  }

  /**
   * Check if the button should be disabled
   *
   * @param plan - Plan type (basic, premium, or platinum)
   * @returns True if button should be disabled
   */
  const isButtonDisabled = (
    plan: 'basic' | 'premium' | 'platinum',
  ): boolean => {
    return !!(
      loadingPlan === plan ||
      subscriptionLoading ||
      getSubscriptionAction(plan) === 'current' ||
      hasPaymentIssue ||
      hasIncompleteSubscription
    )
  }

  /**
   * Calculate the price for a given plan using the centralized helper function
   * @param plan - Plan type (basic, premium, or platinum)
   * @returns Price with annual discount applied if applicable
   */
  const calculatePrice = (plan: 'basic' | 'premium' | 'platinum') => {
    const credits =
      plan === 'basic'
        ? basicCredits
        : plan === 'premium'
          ? premiumCredits
          : platinumCredits
    const domains =
      plan === 'basic'
        ? basicDomains
        : plan === 'premium'
          ? premiumDomains
          : platinumDomains

    return calculatePricingV3Price({
      plan,
      credits,
      domains,
      billingInterval: isAnnual ? 'yearly' : 'monthly',
    })
  }

  const getBasicPrice = () => {
    return calculatePrice('basic')
  }
  const getPremiumPrice = () => {
    return calculatePrice('premium')
  }
  const getPlatinumPrice = () => {
    return calculatePrice('platinum')
  }

  /**
   * Toggle FAQ item open/closed state
   * @param index - Index of FAQ item to toggle
   */
  const toggleFaqItem = (index: number) => {
    setOpenFaqItems((prev) =>
      prev.includes(index)
        ? prev.filter((item) => item !== index)
        : [...prev, index],
    )
  }

  /**
   * Handle opening billing portal for payment updates
   */
  const handleUpdatePayment = async () => {
    try {
      const res = await fetch('/api/stripe/billing-portal?returnJson=true')
      if (!res.ok) {
        throw new Error('Failed to fetch billing portal URL')
      }
      const data = await res.json()
      if (data.url) {
        window.open(`${data.url}#payment-methods`, '_blank')
      }
    } catch (error) {
      console.error('Failed to open billing portal:', error)
      toast.error('Failed to open payment portal. Please try again.')
    }
  }

  /**
   * Handle subscription checkout
   * @param plan - Plan type to subscribe to (basic, premium, or platinum)
   */
  const handleSubscribe = async (plan: 'basic' | 'premium' | 'platinum') => {
    if (loadingPlan === plan) return

    const action = getSubscriptionAction(plan)

    // 如果是当前计划，不执行任何操作
    if (action === 'current') {
      return
    }

    setLoadingPlan(plan)
    try {
      const isUpgradeOrDowngrade =
        action === 'upgrade' || action === 'downgrade' || action === 'change'
      const coupon = getRewardfulCouponId()
      const refId = getRewardfulRefId()

      const credits =
        plan === 'basic'
          ? basicCredits
          : plan === 'premium'
            ? premiumCredits
            : platinumCredits
      const domains =
        plan === 'basic'
          ? basicDomains
          : plan === 'premium'
            ? premiumDomains
            : platinumDomains
      const billingInterval = isAnnual ? 'yearly' : 'monthly'

      // Calculate amount for tracking
      const amount = calculatePricingV3Price({
        plan,
        credits,
        domains,
        billingInterval,
      })

      // Create subscription details for tracking
      const subscriptionDetails: SubscriptionDetails = {
        plan,
        credits: Number.parseInt(credits, 10),
        domains: Number.parseInt(domains, 10),
        billing_interval: billingInterval,
        amount,
        currency: 'USD',
      }

      // Track begin_checkout event
      const item = createEcommerceItem(subscriptionDetails)
      trackBeginCheckout(item, amount)

      // Collect tracking params (gclid, fbclid, etc.)
      const trackingParams = await collectTrackingParams()

      const response = await fetch('/api/pricing-v3/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan,
          credits,
          domains,
          billingInterval,
          isUpgrade: isUpgradeOrDowngrade,
          workspaceId: selectedWorkspaceId, // Pass selected workspace ID
          ...(coupon ? { coupon } : {}),
          ...(refId ? { refId } : {}),
          // Add tracking parameters
          trackingParams,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        // Special handling for payment update needed
        if (data.needsPaymentUpdate && data.status) {
          toast.error(data.error, {
            duration: 10000,
            action: {
              label: 'Update Payment',
              onClick: handleUpdatePayment,
            },
          })
          return
        }
        throw new Error(data.error || 'Failed to create checkout session')
      }

      // 如果是直接升级成功（不需要重定向）
      if (data.success && data.message) {
        if (data.isScheduledChange) {
          toast.success(
            `${data.message}. You can cancel this change anytime before it takes effect.`,
          )
        } else {
          toast.success(data.message)
        }
        // 重新获取subscription状态
        await mutateWorkspaces()
        return
      }

      if (data.url) {
        // 重定向到 Stripe Checkout
        window.location.href = data.url
      } else {
        throw new Error('No checkout URL returned')
      }
    } catch (error) {
      console.error('Checkout error:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to start checkout process. Please try again.',
      )
    } finally {
      setLoadingPlan(null)
    }
  }

  return (
    <div className={'min-h-screen bg-[#212121]'}>
      {/* Payment Success Tracker */}
      <PaymentSuccessTracker />

      {/* Hero Section */}
      <PricingHero
        trigger={trigger}
        workspaceSelector={
          isLoggedIn ? (
            <WorkspaceSelector
              workspaces={workspaces}
              selectedWorkspaceId={selectedWorkspaceId}
              onWorkspaceChange={setSelectedWorkspaceId}
            />
          ) : null
        }
        showCreditUsageLink={isLoggedIn && currentPlan !== 'free'}
      />

      {/* Billing Toggle - Top level control */}
      <BillingToggle
        isAnnual={isAnnual}
        setIsAnnual={setIsAnnual}
        className="container mx-auto px-4 pb-8"
      />

      {/* Incomplete Subscription Banner - Show first if exists */}
      {hasIncompleteSubscription && incompleteSubscription && (
        <IncompleteSubscriptionBanner subscription={incompleteSubscription} />
      )}

      {/* Payment Issue Banner - Only show if no incomplete subscription */}
      {hasPaymentIssue && curSubscription && !hasIncompleteSubscription && (
        <PaymentIssueBanner subscription={curSubscription} />
      )}

      {/* Pricing Cards */}
      <PricingCards
        isModal={isModal}
        isAnnual={isAnnual}
        basicCredits={basicCredits}
        setBasicCredits={setBasicCredits}
        premiumCredits={premiumCredits}
        setPremiumCredits={setPremiumCredits}
        platinumCredits={platinumCredits}
        setPlatinumCredits={setPlatinumCredits}
        basicDomains={basicDomains}
        setBasicDomains={setBasicDomains}
        premiumDomains={premiumDomains}
        setPremiumDomains={setPremiumDomains}
        platinumDomains={platinumDomains}
        setPlatinumDomains={setPlatinumDomains}
        loadingPlan={loadingPlan}
        getBasicPrice={getBasicPrice}
        getPremiumPrice={getPremiumPrice}
        getPlatinumPrice={getPlatinumPrice}
        getButtonText={getButtonText}
        getSubscriptionAction={getSubscriptionAction}
        isButtonDisabled={isButtonDisabled}
        handleSubscribe={handleSubscribe}
        basicCreditOptions={basicCreditOptions}
        premiumCreditOptions={premiumCreditOptions}
        platinumCreditOptions={platinumCreditOptions}
        domainOptions={domainOptions}
        showDomainSelect={shouldShowDomainSelect}
        minRequiredPlan={minRequiredPlan}
        currentPlan={currentPlan}
        subscriptionStatus={subscriptionStatus}
        hasPaymentIssue={hasPaymentIssue}
        hasIncompleteSubscription={hasIncompleteSubscription}
        showEnterpriseCard={showEnterpriseCard}
        showDomainToggle={showDomainToggle}
        setShowDomainToggle={setShowDomainToggle}
        curDomains={curDomains}
        hideFreeAndBasic={trigger === 'workspace-member-management'}
      />

      {/* Feature Comparison Table */}
      <FeatureComparisonTable />

      {/* Understanding AI Credits Section */}
      <UnderstandingCredits />

      {/* FAQ Section */}
      <PricingFAQ openFaqItems={openFaqItems} toggleFaqItem={toggleFaqItem} />
    </div>
  )
}
